/**
 * The base of all pages.
 *
 * @module app/pages/BasePage
 */
import _ from 'lodash';
import jQuery from 'jquery';
import moment from 'moment-timezone';

import { getFeatureParamsFromCookie } from '@wh/feature-toggle-detector';
import rn from '@wh/cp-integration-helper';

import { get, isUserAgentWithDeviceId } from '../../utils';
import BackButton from '../components/BackButton';
import Date from '../components/Date';
import DeviceDetector from '../components/DeviceDetector';
import IconCarousel from '../components/IconCarousel';
import IntervalManager from '../components/IntervalManager';
import MessageBusTopics from '../../common/MessageBusTopics';
import ModelComposer from '../components/ModelComposer';
import OddsFormatProvider from '../components/OddsFormatProvider';
import PageCommonModule from '../pages/PageCommon';
import PageDependencies from '../components/PageDependencies';
import Performance from '../components/Performance';
import PreferencesProvider from '../components/PreferencesProvider';
import ProductMenu from '../components/ProductMenu';
import SelectionViewAdapter from '../views/SelectionViewAdapter';
import DomPushAdapter from '../components/push/domUpdates/DomPushAdapter';
import PushAdapter from '../components/push/PushAdapter';
import LivescoreDiffusionAdapter from '../components/push/LivescoreDiffusionAdapter';
import Translator from '../components/Translator';
import UrlArgsProvider from '../components/UrlArgsProvider';
import UrlQueryStringProvider from '../components/UrlQueryStringProvider';
import XsellIntegratorLoader from '../components/XsellIntegratorLoader';
import SportsDataRepository from '../components/SportsDataRepository';
import InPageNavigation from '../components/InPageNavigation';
import CrossSell from '../components/crossSell/CrossSell';
import BannersPopup from '../components/banners/BannersPopup';
import BannersCarousel from '../components/banners/BannersCarousel';
import BannersAddToSlip from '../components/banners/BannersAddToSlip';
import searchController from '../components/search/SearchController';
import SidebarNavigation from '../components/SidebarNavigation';
import HeaderNavigation from '../components/HeaderNavigation';
import Toolbar from '../components/Toolbar';
import { hidePagePreloader, showPagePreloader } from '../libs/PagePreloaderHelper';
import { iosDeviceIds } from '../constants/deviceIds';

let PageCommon = PageCommonModule;

/**
 * Publish performance timings.
 *
 @type {Function}
 * @returns {void}
 * @private
 */
const publishTimings = _.once(Performance.publishTimings);

/**
 * Cache of the main elements = main container and mask. Upon initialization this will contain the keys $main
 * and $mask
 *
 * @type {Object}
 *
 * @private
 */
var mainContainers = {};

/**
 * Boolean indicating whether we are currently actioning a back button click
 *
 * @type {Boolean}
 * @private
 */
let browserBackInProgress = false;

/**
 * Previous event ID used for the scroll to event when back button is clicked.
 *
 * @type {String}
 * @private
 */
let prevEventId;

/**
 * Speed in ms for the scroll to top/event action.
 *
 * @type {Int}
 * @private
 */
const scrollAnimationSpeed = 250;

/**
 * Body height in pixels. Used to trigger lazy load.
 *
 * @type {Int}
 * @private
 */
const bodyElementHeight = document.body.scrollHeight;

/**
 * Map of selectors used for jQuery and DOM lookups.
 *
 * @type {Object}
 * @private
 */
const SELECTORS = {
    HEADER: 'header',
    SUBHEADER: 'subheader-desktop',
    SCROLL_CONTAINERS: 'html, body, .off-canvas__main',
    SCROLL_CONTAINERS_WITH_SIDEBAR: 'html, body, .off-canvas__main, #desktop-sidebar',
};

/**
 * Returns height in pixels of the selector element.
 *
 * @param {String} selector
 * @return {Int}
 * @private
 */
const getHeight = (selector) =>
    document.getElementById(selector) && document.getElementById(selector).clientHeight;

/**
 * Height of header and subheader
 *
 * @type {Int}
 * @private
 */
const headerAndSubheaderHeight = getHeight(SELECTORS.HEADER) + getHeight(SELECTORS.SUBHEADER);

/**
 * Add an event listener for back button clicks that sets the back button click state to in progress
 * and the prevEventId to the current eventId at the time of the click.
 *
 * @private
 */
const listenForBrowserBackAction = () =>
    window.addEventListener('popstate', () => {
        browserBackInProgress = true;
        prevEventId = window.WH.sportsbook.eventId;
    });

/**
 * Checks if the back button had been pressed and there is an previous event id. If it finds both scrolls to that
 * specific event, if not scrolls to the top of the page.
 *
 * @returns {Function}
 * @private
 */
const handleBrowserBackAction = () =>
    (browserBackInProgress && prevEventId) ? scrollToEvent(prevEventId) : scrollTop(true);

/**
 * Clear global variables for browser back state.
 *
 * @private
 */
const resetBrowserBackAction = () => {
    browserBackInProgress = false;
    prevEventId = null;
};

/**
 * Scrolls selected containers up to the top and and finishes the browser back action.
 *
 * @param {Boolean} addSideBar Flag for adding the sidebar to the scrolled containers.
 * @public
 */
const scrollTop = (addSideBar) => {
    jQuery(addSideBar ?
        SELECTORS.SCROLL_CONTAINERS_WITH_SIDEBAR : SELECTORS.SCROLL_CONTAINERS)
        .animate({ scrollTop: 0 }, scrollAnimationSpeed);

    resetBrowserBackAction();
};

/**
 * Scrolls to a html element and then drops futher so the event is not obscured by the header/subheader.
 * and finishes the browser back action.
 *
 * @param {Object} element
 */
const scrollIntoView = (element) => {
    element.scrollIntoView(true);
    window.scrollBy(0, -headerAndSubheaderHeight);

    resetBrowserBackAction();
};

/**
 * If tabs are visable does not attempt lazy load. This could be extended to switch tab before checking for
 * event again.
 *
 * @param {String} eventId
 */
const lazyLoadOrKillSwitch = (eventId) =>
    DeviceDetector.isDesktop() ? lazyLoadAndAttemptToScroll(eventId) : scrollTop(true);

/**
 * Scrolls to an event if one is in the DOM. If it is not present, checks if we are dealing with mobile/tablet
 * and gives up and scrollsTop, if desktop it triggers a lazy load of more events then tries to locate again.
 *
 * @param {String} eventId
 */
const scrollToEvent = (eventId) => {
    const eventExists = document.getElementById(eventId);

    if (eventExists) {
        scrollIntoView(eventExists);
    } else {
        lazyLoadOrKillSwitch(eventId);
    }
};

/**
 * Triggers a page rerender for lazy loading, listens for the spinner to finish then attempts to scroll to
 * the event if it finds it. Failing that scrolls to top.
 *
 * @param {String} eventId
 */
const lazyLoadAndAttemptToScroll = (eventId) => {
    window.scrollBy(0, bodyElementHeight);

    const messageBusRef = window.WH.messageBus.subscribe(MessageBusTopics.PRELOADER_TOPIC, (topic, payload) => {
        if (payload.data === false) {
            window.waitFor(
                () => document.getElementById(eventId),
                400,
                10,
                () => {
                    scrollIntoView(document.getElementById(eventId));
                    window.WH.messageBus.unsubscribe(messageBusRef);
                },
                () => {
                    scrollTop(true);
                    window.WH.messageBus.unsubscribe(messageBusRef);
                }
            );
        }
    });
};

/**
 * Hides any visible child page links and unhides the child page links for for the new sport.
 * Also add the expanded class to the expanded section
 *
 * @param   sport   {String}    The slug (english) for the sport we have navigated to
 *
 * @returns {void}
 *
 * @private
 */
var updateSidebarContext = function updateSidebarContext(localArgs) {

    var $sidebar = jQuery('#desktop-sidebar');

    if (_.size($sidebar) > 0 && _.isString(localArgs.sport)) {

        // There are some weird exceptions where the sport param doesn't match the sport slug, handle them here
        var sport = getCorrectedSport(localArgs);
        var page = getCorrectedPage(localArgs);
        var sidebarItemKey = sport;
        if (page === 'inplay') {
            sidebarItemKey = 'in-play';
        }

        // remove all the active class from the list items
        $sidebar.find('.c-list__item--active').removeClass('c-list__item--active');
        // add the expanded class to the parent
        $sidebar.find('#nav-' + sidebarItemKey + ' a').addClass('c-list__item--active');

        updateChildPageActiveClass(localArgs, sport, $sidebar);
        updatePopularLinks(sport, $sidebar, page);
        updateSidebarContextTracking();
    }
};

/**
 * Rebind the click tracking when the contextual navigation is added
 *
 * @returns {void}
 *
 * @private
 */
var updateSidebarContextTracking = function updateSidebarContextTracking() {
    jQuery('#desktop-sidebar').find('#sidebar-left-context a')
        .off('click.sidebarTracking')
        .on('click.sidebarTracking', (event) =>
            publishNavClick('context', jQuery(event.currentTarget).attr('title'))
        );
};

/**
 * Bind the links in the left-hand nav to a message bus publish on click
 *
 * @returns {void}
 *
 * @private
 */
var bindTracking = function bindTracking() {
    var $sidebar = jQuery('#desktop-sidebar');
    var $sidebarNavSection = $sidebar.find('[id*="desktop-sidebar-"]:not("#desktop-sidebar-crosssell")');
    $sidebarNavSection.each(function (index, element) {
        const name = element.id.replace('desktop-sidebar-', '');
        jQuery(element).find('a')
            .off('click.sidebarTracking')
            .on('click.sidebarTracking', function (event) {
                publishNavClick(name, jQuery(event.currentTarget).attr('title'));
            });
    });
};

/**
 * Publish a message onClick for tracking purposes
 *
 * @param {string} navigationArea The area of the left-hand nav
 *
 * @param {string} navItem The specific menu item
 *
 * @returns {void}
 *
 * @private
 */
var publishNavClick = function publishContext(navigationArea, navItem) {
    window.WH.messageBus.publish(MessageBusTopics.NAVIGATION_CLICKED, { navigationArea, navItem });
};

/**
 * Finds the popular links DOM element from the sidebar and hides the currently
 * selected sport if it is in the list. Reveals the previously hidden sport.
 * Note that popular links are generated in the template based on data from Squiz,
 * the id is based on the popularLink.slug and in some case the sport part used in the id
 * will not match the sport we are passed (e.g. Squiz sends 'horses' instead of 'horse-racing')
 * so we correct for that here.
 * It also currently does not send a slugEn, which means that in different locales this is
 * not going to work properly (Hiding selected sport)
 *
 * @param {String} sport The slug (english) for the sport we have navigated to
 * @param {Object} sidebar jQuery ref for the sidebar DOM element
 */
var updatePopularLinks = function updatePopularLinks(sport, sidebar, page = '') {

    var popularLinks = sidebar.find('#desktop-sidebar-quick-links');

    if (_.size(popularLinks) > 0) {
        // Correct exceptions in generated '#nav-[sport]' based on data from squiz
        var correctedSport = sport.replace('horse-racing', 'horses');

        var popularLinksItemKey = correctedSport;
        if (page === 'inplay') {
            popularLinksItemKey = 'in-play';
        }

        popularLinks.find('.displayNone').removeClass('displayNone');
        popularLinks.find('#nav-' + popularLinksItemKey + ' a').addClass('displayNone');
    }
};
/**
 * Adds the class to the sidebar child page link to mark it as active
 *
 * @param {Object}  localArgs   Param from the page change event
 * @param {String}  sport       Corrected sport name
 * @param {Object}  sidebar     jQuery ref for the sidebar DOM element
 *
 * @returns {void}
 *
 * @private
 */
var updateChildPageActiveClass = function updateChildPageActiveClass(localArgs, sport, sidebar) {

    var page = getCorrectedPage(localArgs);
    var exceptions = ['highlights', 'meetings'];

    sidebar.find('#sidebar-left-context .c-list__item--active').removeClass('c-list__item--active');

    if (localArgs.competitionName === 'YourOdds') {
        sidebar.find('#nav-football-yourOdds' + ' a').addClass('c-list__item--active');
    } else if (_.isString(page) && !_.includes(exceptions, page)) {
        sidebar.find('#nav-' + sport + '-' + page + ' a').addClass('c-list__item--active');
    }
};

/**
 * Takes the local arguments as parameter from the change page event and applies any edge case corrections we may
 * need.
 *
 * @param {Object}  localArgs   The localArgs object
 *
 * @returns {String}        The corrected sport name
 *
 * @private
 */
var getCorrectedSport = function getCorrectedSport(localArgs) {

    var correctedSport = localArgs.sport;

    if (localArgs.page === 'virtual') {

        correctedSport = 'virtual-world';
    } else {

        correctedSport = correctedSport.replace(/^top-bets-home$/, 'top-bets');
        correctedSport = correctedSport.replace(/^inPlay$/, 'in-play');
    }

    return correctedSport;
};

/**
 * Takes the local arguments as parameter from the change page event and applies any edge case corrections we may
 * need.
 *
 * @param {Object}  localArgs   The localArgs object
 *
 * @returns {String}        The corrected page name
 *
 * @private
 */
var getCorrectedPage = function getCorrectedPage(localArgs) {

    var correctedPage = localArgs.page;

    if (localArgs.sport === 'virtual-world') {

        correctedPage = 'virtual' + localArgs.sportclass + localArgs.subclass;
    }
    correctedPage = correctedPage.toLowerCase()
        .replace('future-races', 'antepost')
        .replace(/-/g, '');

    return correctedPage;
};

/**
 * Function used for rendering a page
 *
 * @param   {Object} page Page to render
 * @param   {Object} deps Page dependencies
 * @param   {Object} args Page arguments
 *
 * @returns {void}
 *
 * @private
 */
var renderPage = function (page, deps, args) {

    /**
     * Render new page partial function
     *
     * @returns {void}
     */
    var renderNewPage = function renderNewPage() {

        var partialData = null;
        var dependenciesLoaded = false;

        /**
         * Check if both request were fulfilled, if so call final callback
         */
        var tryResolve = function tryResolve() {

            if (partialData && dependenciesLoaded) {

                callbacks.onSuccess(partialData);
                updateSidebarContext(args);
            }
        };

        /**
         * Resolve partial's callback function
         *
         * @param {Object} data partial request response
         */
        var partialCallback = function partialCallback(data) {

            partialData = data;
            tryResolve();
            handleBrowserBackAction();
        };

        /**
         * Resolve dependencies's callback function
         */
        var dependenciesCallback = function dependenciesCallback() {

            dependenciesLoaded = true;
            tryResolve();
        };

        request(
            page.getContentURL(args),
            partialCallback,
            callbacks.onError
        );

        PageDependencies.loadPageDependencies(args.page, dependenciesCallback);
    };

    /**
     * Render initial loaded page
     *
     * @returns {void}
     */
    var reRenderPage = function reRenderPage() {

        showPagePreloader();

        /**
         * Called when all dependencies were loaded and executed
         */
        var loadPageDependenciesCallback = function loadPageDependenciesCallback() {

            postContentLoad(page, content, deps, args);
            handleBrowserBackAction();
        };

        PageDependencies.loadPageDependencies(args.page, loadPageDependenciesCallback);
    };

    var callbacks = {

        /**
         * Function called upon a successful request
         *
         * @param   {[type]} data [description]
         *
         * @returns {[type]}      [description]
         *
         * @private
         */
        onSuccess: function (data) {

            handleRedirect(data);

            content.html(data);

            postContentLoad(page, content, deps, args);

            OddsFormatProvider.select();
            PageCommon.oddsConvert(false);
        },

        /**
         * Function called when an errors occurs
         * during the request
         *
         * @param   {Object} jqXHR   [description]
         * @param   {[type]} status  [description]
         * @param   {[type]} message [description]
         *
         * @returns {[type]}         [description]
         *
         * @private
         */
        onError: function (jqXHR, status, message) {
            if (jqXHR.status === 404) {
                content.html(jqXHR.responseText);

                postContentLoad(page, content, deps, args);
            }

            hidePagePreloader();

            window.WH.messageBus.publish(MessageBusTopics.PRELOADER_TOPIC, {
                operation: MessageBusTopics.PRELOADER_OPERATION_FULL_PAGE_ERROR,
                data: {
                    message: message,
                    content: content,
                },
            });

            page.setRendered();
        },
    };

    var content = PageCommon.getContent();

    if (page.isRendered() === false) {

        renderNewPage();
    } else {

        reRenderPage();
    }
};

/**
 * Operations to perform after content has been loaded.
 * @returns {void}
 * @private
 */
var postContentLoadOperations = function () {

    if (window.WH && window.WH.messageBus) {

        window.WH.messageBus.publish(MessageBusTopics.NAVIGATION_PAGE_CHANGED);

        hidePagePreloader();

        window.WH.messageBus.publishSticky(MessageBusTopics.TRANSLATION_LOCALE, window.WH.i18n.locale);

        publishTimings();
        initDeferredDeps();
    }
};

/**
 * Inits the betslip dependencies if not in native env
 *
 * @returns {void}
 *
 * @private
 */
var initDeferredDeps = async function () {
    const { default: BetBuilderModal } = await import('../components/BetBuilderModal');
    BetBuilderModal.init();

    if (!window.native) {
        const { default: DeferredDependencies } = await import('../components/DeferredDependencies');
        await DeferredDependencies.init();
    }
};

/**
 * Register listener on body that will prevent default behaviour on .link-prevent-click
 * class
 *
 * @returns {void}
 *
 * @private
 */
var initLinkPreventDefault = function initLinkPreventDefault() {

    var jQueryElement = jQuery('#main');

    jQueryElement
        .off('click.link-prevent-click', preventClick)
        .on('click.link-prevent-click', preventClick);
};

/**
 * Prevents default behaviour, used for Click
 *
 * @param   {Object} e  Event object
 *
 * @private
 */
var preventClick = function preventClick(e) {

    if (jQuery(e.target).closest('.link-prevent-click').length > 0) {
        e.preventDefault();
    }
};

/**
 * Sets the rightnow help methods on the window object
 * @return {Promise.<void>}
 */
const bindCustomerServicesMethods = async () => {
    Object.assign(window, rn);
};

/**
 * Function initializes CrossSell in CommonModules
 *
 * @returns {void}
 *
 * @private
 */
const initCommonModules = function () {
    CrossSell.init(jQuery);
    bindCustomerServicesMethods();
    Toolbar.init();
};

/**
 * Lazily initialise banners
 * @return {Promise.<void>}
 * @private
 */
const initBanners = () => {
    BannersPopup.init();
    BannersCarousel.init();
    BannersAddToSlip.init();
    ProductMenu.init();
    IconCarousel.init();
};

/**
 * Lazily initialise search
 * @return {Promise.<void>}
 * @private
 */
const initSearch = () => {
    searchController.init({
        Date: Date,
        Location: window.location,
        Translator: Translator,
        DeviceDetector: DeviceDetector,
        OddsFormatProvider: OddsFormatProvider,
        SelectionViewAdapter: SelectionViewAdapter,
        UrlQueryStringProvider: UrlQueryStringProvider,
        PageCommon: PageCommon,
    });
};

/**
 * Lazily initialise the product menu
 * @return {Promise.<void>}
 * @private
 */
const initProductMenu = () => {
    ProductMenu.init();
};

/**
 * Lazily initialise in page navigation
 * @return {Promise.<void>}
 * @private
 */
const initInPageNavigation = (mockPageCommon) => {
    const containers = [
        'div.outermain',
        '#desktop-sidebar',
        '#search-overlay',
        '#topBetsFragmentContainer',
    ];
    InPageNavigation.init(containers.join(', '), mockPageCommon);
};

/**
 * Lazily initialise sidebar navigation
 * @return {Promise.<void>}
 * @private
 */
const initSidebarNavigation = (PageCommon) => {
    SidebarNavigation.init(PageCommon);
};

/**
 * Lazily initialise Header navigation
 * @return {Promise.<void>}
 * @private
 */
const initHeaderNavigation = (PageCommon) => {
    HeaderNavigation.init(PageCommon);
};

/**
 * Function initializes components
 * @returns {void}
 * @private
 */
var initComponents = function initComponents(mockPageCommon, PageCommon) {
    BackButton.init();
    XsellIntegratorLoader.init();
    initInPageNavigation(mockPageCommon);
    initSidebarNavigation(PageCommon);
    initHeaderNavigation(PageCommon);
    initBanners();
    initProductMenu();
    initSearch();
    if (get(window, 'WH.i18n.locale') === 'en-gb' && jQuery('.wh-radio.js-wh-radio').length) {
        import(/* webpackChunkName: "WHRadioController" */'../components/WHRadioController');
    }
};

/**

* Function adds a stylesheet if Android Native app version is 4.4.0 and above.

*/

const adjustStylesAndroid = () => {
    const style = document.createElement('style');
    style.id = 'bottom-toolbar-android-fix';
    style.textContent = `
    #betslip-ui .bs-betslip {
        bottom: 0;
    }
    #games-lobby-container {
        bottom: 0;
    }
    #footer-root .footer-wrapper-mobile {
        padding-bottom: 0;
    }
    `;
    document.body.append(style);
};

/**
 * Function hides the bottom toolbar if Android Native app version is 4.4.0 and above.
 *
 * @param   {boolean} isNativeAndroid
 * @param   {boolean} isIos
 * @param   {string} appVersionString
 *
 * @returns {void}
 *
 * @private
 */
var removeToolbarAndroidNative = function (isNativeAndroid, isIos, appVersionString) {
    try {
        if (isNativeAndroid && !isIos && appVersionString) {
            const [major, minor] = appVersionString.split('.').map(str => Number(str));
            if (major > 4 || (major === 4 && minor >= 3)) {
                jQuery('.toolbar').addClass('wh-hide');
                adjustStylesAndroid();
            }
        }
    } catch (error) {
        console.error(error);
    }
};

var hideSearchIconAndAddSearchBarForIosNative = function (appVersionString, isNativeComJurisdiction) {
    try {
        const [major, minor] = appVersionString.split('.').map(str => Number(str));
        if (!isNativeComJurisdiction) {
            return;
        }
        if (major >= 10 && isNativeComJurisdiction) {
            // Hide the search icon and add search bar for iOS native app v10 and above on .com.
            // https://jira.willhillatlas.com/browse/IOS-153
            jQuery('html').addClass('dd10plus');
        }
        if (major >= 12 && minor >= 4 || major >= 13) {
            jQuery('html').addClass('dd12dot4plus');
        }
    } catch (error) {
        console.error(error);
    }
};

/**
 * Function sets class attributes based on DeviceDetector
 *
 * @returns {void}
 *
 * @private
 */
var initDetectionMarkup = function initDetectionMarkup() {
    var appVersionString = DeviceDetector.getAppVersion();
    var isNative = DeviceDetector.isNative();
    var isNativeiOS = DeviceDetector.isNativeiOS();
    var isIos = DeviceDetector.isIOS();
    var isNativeComJurisdiction = DeviceDetector.isNativeCom();
    var isNativeAndroid = DeviceDetector.isNative() && DeviceDetector.isAndroid();
    var isInternationalJurisdiction = DeviceDetector.isInternational();

    if (isNativeiOS && appVersionString) {
        hideSearchIconAndAddSearchBarForIosNative(appVersionString, isNativeComJurisdiction);
    } else {
        removeToolbarAndroidNative(isNativeAndroid, isIos, appVersionString);
    }

    if (isInternationalJurisdiction) {
        /*
        Temporary solution:
        It should be refactored to be called only for isNativeIos, right after
        the trafficscript responsible for setting the wh_device cookie for
        international platform is reliable. Meaning, this issue is done:
        https://jira.willhillatlas.com/browse/MRGIOSAPPS-1192
        Now, being in native iOS app, we get cookies set wrongly:
        1. wh_device.device_os == 'ios' && wh_device.is_native == false
        2. wh_device.device_os == 'desktop && wh_device.is_native == true
        The condition covers for those scenarios.
        */
        if (isIos || isNative) {
            /*
            Temporary solution:
            We check ios device Ids which are appended to user agent strings,
            because we can't rely on appVersion cookie in international platform as of today.
            Com & International handle request headers & cookies differently.
            https://jira.willhillatlas.com/browse/WHINTS-574
            */
            const isIosDeviceWithId = isUserAgentWithDeviceId(window.navigator.userAgent, iosDeviceIds);
            if (isIosDeviceWithId) {
                // Hide web's elements: header, toolbar & sports menu
                // https://jira.willhillatlas.com/browse/WHINTS-704
                jQuery('html').addClass('ddIntlVersion4plus');
                jQuery('#header').addClass('wh-hide');
                jQuery('.toolbar').addClass('wh-hide');
            }
        }
    }
    hideLanguageSelector(DeviceDetector.isNative());

    window.WH.messageBus.publishSticky(MessageBusTopics.DETECTION_MARKUP_INIT_COMPLETE);
};

/**
 * Hides language selector for native app
 *
 * @param {boolean} isNative device detector boolean value for native cookie
 *
 * @returns {void}
 *
 * @private
 */
var hideLanguageSelector = function hideLanguageSelector(isNative) {

    if (isNative) {

        jQuery('footer.footer__main').find('.languageSelectionToggle').addClass('wh-hide');
    }
};

/**
 * Inits the main containers element cache to avoid querying the DOM on every change
 *
 * @returns {void}
 *
 * @private
 */
var initMainContainers = function () {

    mainContainers.$mainCenter = jQuery('main.maincenter');
    mainContainers.$mainCenterMask = mainContainers.$mainCenter.find('div.mask');
};

/**
 * Checks for the main container cache and adds it if missing
 *
 * @returns {void}
 *
 * @private
 */
var ensureContainer = function ensureContainer() {

    if (!mainContainers.$main) {

        initMainContainers();
    }
};

/**
 * Additional initialization after page content load using ajax.
 *
 * @returns {void}
 *
 * @private
 */
var postContentLoadSubInit = function postContentLoadSubInit() {

    PageCommon.IntegrationGlobals.initialise(window);

    initLinkPreventDefault();

    postContentLoadOperations();
};

/**
 * This function makes the necessary operations after the page content is being
 * loaded either using ajax or from a first load.
 *
 * @param   {Object} content DOM object
 * @param   {Object} page Page to render
 * @param   {Object} deps Page dependencies
 * @param   {Object} args Page arguments
 *
 * @returns {void}
 *
 * @public
 */
var postContentLoad = function (page, content, deps, args) {

    handleContentReady(page, content, deps, args);
    page.setRendered();
    ModelComposer.generateMergedModel();

    SportsDataRepository.clearDomIndex();
    SportsDataRepository.indexDom();

    page.postRender(deps, content, args);

    postContentLoadSubInit();
};

/**
 * Bind navigation handler to buttons
 * on the page
 *
 * @param   {Object} content jQuery element
 * @param   {Object} deps    Page dependencies
 * @param   {Object} args    Page arguments
 *
 * @returns {void}
 *
 * @private
 */
var bindNavigation = function (content) {

    /**
     * Bind navigation handler to an element based
     * on its selector
     *
     * @param   {String} selector Element selector
     *
     * @returns {void}
     *
     * @private
     */
    var bindToSelector = function (selector) {

        content.find(selector + ':not(.non-route)')
            .off('click.basePage')
            .on('click.basePage', PageCommon.navigate);
    };

    [
        '.localnavigation__nonmobile a',
        '.localnavigation-dropdown a',
    ].forEach(bindToSelector);

};

/**
 * Make an AJAX request
 *
 * @param   {String} url       URL to request
 * @param   {Function} onSuccess Success callback function
 * @param   {Function} onError   Error callback function
 *
 * @returns {void}
 *
 * @private
 */
var request = function (url, onSuccess, onError) {
    if (url.indexOf('/fragments/') === -1) {
        url = addAjaxSuffix(url);
    }
    url = addAjaxDomain(url, window.WH.sportsbook);

    var queryParams = [];
    var featureParams = getFeatureParamsFromCookie();
    var abFeatureParams = getFeatureParamsFromCookie('public');

    if (featureParams && featureParams.length) {
        queryParams.push(featureParams);
    }

    if (abFeatureParams && abFeatureParams.length) {
        queryParams.push(abFeatureParams);
    }

    jQuery.ajax({
        url: url,
        dataType: 'html',
        success: onSuccess,
        error: onError,
        data: queryParams.join('&'),
    });
};

/**
 * Call the page setup functions
 *
 * @param {Object} page Page
 * @param {Object} content jQuery Element
 * @param {Object} deps Page dependencies
 * @param {Object} args Page arguments
 *
 * @return {void}
 *
 * @private
 */
var setupPage = function (page, content, deps, args) {

    page.args = args;
    BackButton.currentPage = page;

    handleSportsbookRedirect(window.WH.sportsbook || {});
    injectDependencies(deps);
    bindNavigation(jQuery('body'), deps, args);

    page.bindNavigation(content, deps, args);
    page.postReady(deps, content, args);
};

/**
 * Function used for handling the page & content after
 * it has been added to the DOM
 *
 * @param   {Object} page    Page
 * @param   {Object} content jQuery element
 * @param   {Object} deps    Page dependencies
 * @param   {Object} args    Page arguments
 *
 * @returns {void}
 *
 * @private
 */
var handleContentReady = function (page, content, deps, args) {

    setupPage(page, content, deps, args);

    PageCommon.oddsConvert(false);
    manageHiddenEntities(deps);
};

/**
 * Not quite sure why it does what it does
 *
 * @param   {String} data HTML string coming from AJAX
 *
 * @returns {void}
 *
 * @private
 */
var handleRedirect = function (data) {

    if (data.match(/^(.*)DOCTYPE/g)) {
        window.location.replace('/bet/en-gb/betting');
    }
};

/**
 * Function used for handling ajax triggered
 * redirects
 *
 * @param   {Object} WHSports WHSports object
 *
 * @returns {void}
 *
 * @private
 */
var handleSportsbookRedirect = function (WHSports) {

    if (_.isUndefined(WHSports.redirect) === false && !!WHSports.redirect) {
        window.location.replace(WHSports.redirect);
    }
};

/**
 * Injects dependencies into a deps object
 *
 * @param   {Object} deps Page dependencies
 *
 * @returns {void}
 *
 * @private
 */
var injectDependencies = function (deps) {

    _.extend(deps, {
        preferencesProvider: getPreferencesProvider(deps),
    });
};

/**
 * Returns PageCommon in deps or generic one
 *
 * @param   {Object} deps Page dependencies
 *
 * @returns {Object}      PageCommon object
 *
 * @private
 */
var getPageCommon = function (deps) {

    return deps.pageCommon || PageCommon;
};

/**
 * Returns PreferencesProvider in deps or generic one
 *
 * @param   {Object} deps Page dependencies
 *
 * @returns {Object}      PreferencesProvider object
 *
 * @private
 */
var getPreferencesProvider = function (deps) {

    return deps.preferencesProvider || PreferencesProvider;
};

/**
 * Function used to clear subscriptions and intervals
 * for live score, push updates and interval manager
 *
 * @returns {void}
 *
 * @private
 */
var clearSubscriptions = function () {

    LivescoreDiffusionAdapter.unsubscribeAll();
    PushAdapter.unsubscribeAll();
    DomPushAdapter.unsubscribeAll();
    IntervalManager.clearAll();
};

/**
 * Function to add .partial extension to ajax request to stop browsers caching request headers.
 *
 * @param {String} url
 *
 * @returns {string}
 *
 * @private
 */
var addAjaxSuffix = function (url) {

    if (url.indexOf('?') > -1) {
        //if there are get parameters put the extension before the parameters
        var tmp = url.split('?');
        var query = tmp[1] ? '?' + tmp[1] : '';
        return tmp[0] + '.partial' + query;

    } else {
        return url + '.partial';
    }
};

/**
 * Function to add a custom domain for ajax requests
 * if available
 *
 * @param {String} url      Ajax URL
 * @param {Object} WHSports WH.sportbook object
 *
 * @private
 */
var addAjaxDomain = function (url, WHSports) {

    if (typeof WHSports.ajaxDomain !== 'undefined') {
        return WHSports.ajaxDomain + url;
    }

    return url;
};

/**
 * Function assigns a value to a reference object key
 *
 * @param refObject
 * @param val
 * @param key
 */
var assignValueToObjectKey = function (refObject, val, key) {

    refObject[key] = val;
};

/**
 * Handles the subscriptions for every single hidden entity
 * @return {[type]} [description]
 */
var manageHiddenEntities = function (deps) {

    var getHiddenMarkets = function () {
        var result = [];

        if (window.WH.sportsbook.hasOwnProperty('initialHiddenMarketData') &&
            window.WH.sportsbook.initialHiddenMarketData.length > 0) {

            result = window.WH.sportsbook.initialHiddenMarketData;
        }

        return result;
    };

    var data = {
        model: deps.sportsDataRepository.getModel(),
        hiddenMarkets: getHiddenMarkets(),
    };

    // Im doing it not at the subscribing time to avoid fast push messages comming when
    // all the dependencies not present into the model
    var updateModelWithHiddenData = function () {


        var addToArrayUnique = function (arr, value) {
            if (jQuery.inArray(value, arr) === -1) {
                arr.push(value);
            }
        };

        var addMarket = function (market) {

            var addMarketToModel = function () {
                if (!data.model.markets.hasOwnProperty(market.id)) {
                    data.model.markets[market.id] = newMarket;
                }
            };

            var addMarketToParentModel = function () {
                var eventId = market.eventId;
                if (data.model.hasOwnProperty('events') &&
                    data.model.events.hasOwnProperty(eventId)) {

                    addToArrayUnique(data.model.events[eventId].markets, market.id);

                }
            };

            var newMarket = market;

            addMarketToModel();
            addMarketToParentModel();

        };

        var addSelection = function (selection) {

            var addSelectionToModel = function () {
                if (!data.model.selections.hasOwnProperty(selection.id)) {
                    data.model.selections[selection.id] = newSelection;
                }
            };

            var addSelectionToParentModel = function () {
                var marketId = selection.marketId;
                if (data.model.hasOwnProperty('markets') &&
                    data.model.markets.hasOwnProperty(marketId)) {

                    addToArrayUnique(data.model.markets[marketId].selections, selection.id);

                }
            };

            var newSelection = selection;

            addSelectionToModel();
            addSelectionToParentModel();
        };

        var hiddenMarketsCopy = jQuery.extend(true, {}, data.hiddenMarkets);

        jQuery.each(hiddenMarketsCopy, function (i, market) {
            var selections = market.selections;
            var newSelectionsObject = [];

            jQuery.each(selections, function (i, selection) {
                addSelection(selection);
                newSelectionsObject.push(selection.id);
            });

            market.selections = newSelectionsObject;
            addMarket(market);
        });
    };

    var subscribeHiddenMarkets = function () {

        var timeout = 1;

        var subscriptionsDelayer = function (entities) {

            var subscirbeSelections = function (entity) {
                if (entity.hasOwnProperty('selections') && entity.selections.length > 0) {
                    IntervalManager.pushTimeout(
                        setTimeout(function () {
                            subscriptionsDelayer(entity.selections);
                        }, timeout)
                    );
                }
            };

            var subscribe = function () {
                var entity = entities.pop();
                PushAdapter.subscribe(entity.pdsTopic);
                subscirbeSelections(entity);
                IntervalManager.pushTimeout(
                    setTimeout(function () {
                        subscriptionsDelayer(entities);
                    }, timeout)
                );
            };

            if (entities.length > 0) {
                subscribe();
            }

        };

        var clone = jQuery.extend(true, [], data.hiddenMarkets);
        subscriptionsDelayer(clone);

    };

    var start = function () {

        if (data.hiddenMarkets.length > 0) {
            updateModelWithHiddenData();
            deps.sportsDataRepository.setModel(data.model);
            subscribeHiddenMarkets();
        }
    };

    start();
};

/**
 * Bind toggle event for html element by data-toggle attribute and apply to data-name element
 *
 * @returns {void}
 *
 * @private
 */
var bindToggle = function () {
    jQuery('body').on('click', '[data-toggle]', function () {
        var name = jQuery(this).data('toggle');

        var $block = jQuery('[data-name="' + name + '"]');
        var className = $block.data('toggleClass');
        $block.toggleClass(className);
    });
};

/**
 * Contains functions exposed by this component
 * as an API
 *
 * @type {Object}
 */
var api = {

    PageCommon: PageCommon,

    jQuery: jQuery,

    underscore: _,

    moment: moment,

    Date: Date,

    IntervalManager: IntervalManager,

    ModelComposer: ModelComposer,

    oddsConvert: PageCommon.oddsConvert,

    manageHiddenEntities: manageHiddenEntities,

    LazyLoadingViewAdapter: PageCommon.LazyLoadingViewAdapter,

    UrlArgsProvider: UrlArgsProvider,

    UrlQueryStringProvider: UrlQueryStringProvider,

    PreferencesProvider: PreferencesProvider,

    scrollTop: scrollTop,

    /**
     * Function used to display a page, it also checks if left sidebar needs refreshing
     *
     * @type {Function}
     *
     * @public
     */
    display: function (localPage, localArgs, localDeps) {

        UrlArgsProvider.update();

        UrlQueryStringProvider.update();

        localDeps.pageCommon = getPageCommon(localDeps);
        renderPage(localPage, localDeps, localArgs);
        PageCommon.IntegrationGlobals.initialise(window);
        updateSidebarContext(localArgs);
    },

    /**
     * Unload this page.
     * @todo extract this elsewhere.
     *
     * @param {Object} page Page to be unloaded
     *
     * @returns {void}
     *
     * @private
     */
    unload: function (page) {

        PageCommon.getContent()
            // .empty()
            .off('click');
        showPagePreloader();

        page.setUnrendered();

        window.WH.messageBus.publish(MessageBusTopics.NAVIGATION_BEFORE_PAGE_CHANGE);

        clearSubscriptions();
    },

    /**
     * Function used to switch tabs styling
     *
     * @param   {String} tabName Tab name ('Matches', 'Highlights', etc)
     *
     * @returns {void}
     *
     * @public
     */
    switchTab: function (tabName) {

        api.addMainContainerClass(tabName);
    },

    /**
     * set the class on the main container
     *
     * @param {String} tabName The name of the class to add to the main container
     *
     * @returns {void}
     *
     * @private
     */
    addMainContainerClass: function (tabName) {

        var mainClasses = 'highlights competitions matches events home inplay event coupons whtv promotions ' +
            'Matches racing meetings virtual results',
            masks = '-highlights -competitions -matches -events -home -inplay -event -coupons -whtv -promotions ' +
                '-Matches -racing -meetings -virtual',
            racingTabs = [
                'AntePost',
                'Meetings',
                'Specials',
            ];

        ensureContainer();

        mainContainers.$mainCenter
            .removeClass(mainClasses)
            .addClass(tabName.toLowerCase());

        if (racingTabs.indexOf(tabName) < 0) {

            mainContainers.$mainCenterMask
                .removeClass(masks)
                .addClass('-' + tabName.toLowerCase());
        } else {

            mainContainers.$mainCenterMask
                .removeClass(masks)
                .addClass('-racing -' + tabName.toLowerCase());
        }
    },

    /**
     * Init function for BasePage
     *
     * @param   {Object} mockPageCommon Mock page common, if testing the functionality
     *
     * @returns {void}
     *
     * @public
     */
    init: function (mockPageCommon) {

        PageCommon = (typeof mockPageCommon !== 'undefined') ? mockPageCommon : PageCommon;
        PageCommon.init();
        initComponents(mockPageCommon, PageCommon);
        initCommonModules();
        initDetectionMarkup();
        bindTracking();
        bindToggle();
        listenForBrowserBackAction();
        UrlQueryStringProvider.init(window.WH.messageBus);
    },

    /**
     * Function used to set the displayed odds format to the value in the PreferencesProvider
     *
     * @returns {void}
     *
     * @public
     */
    setOddsFormat: function () {

        var oddsFormat = PreferencesProvider.getPreference('oddsFormat');
        jQuery('.odds-format__toggle').attr('data-selected-format', oddsFormat);
        jQuery('.odds-format button').removeClass('-active');
        jQuery('.odds-format button[data-odds-format="' + oddsFormat + '"]').addClass('-active');
    },

    /**
     * Get provided diffusion message and apply it to top-bets widget
     *
     * @param {Object} message
     *
     * @returns {void}
     *
     * @public
     */
    propagateTopBetsBetInRun: function propagateTopBetsBetInRun(message) {

        if (message.attribute === 'betInRun' && message.value === true) {

            jQuery('.topbets #' + message.id).remove();
        }
    },

    /**
     * Function assigns values to a reference object properties
     *
     * @param {Object} refObject Reference Object
     * @param {Object} keyValues An Object that contains refObject properties as keys and values for them
     *
     * @returns {void}
     *
     * @public
     */
    setObjectValues: function (refObject, keyValues) {

        api.underscore.each(keyValues, assignValueToObjectKey.bind(this, refObject));
    },

    /**
     * Getting english slug of sport
     *
     * @param   {Object} event Event model
     *
     * @returns {String}
     *
     * @private
     */
    getSportEnSlug: function getSportEnSlug(event) {

        return event.sportEn || window.WH.sportsbook.sportEn;
    },

    /**
     * Slug to identify the current page
     */
    id: 'base',
};

export default api;
